<script setup>
const props = defineProps({
  items: Array,
  modelValue: Array,
  placeholder: String,
  size: { type: String, default: 'md' }, // 'sm', 'md', 'lg',
  disabled: {
    type: Boolean,
    default: false
  },
  side: {
    type: String,
    default: 'bottom-right'
  },
  searchable: {
    type: Boolean,
    default: true
  },
  showAssignedTo: {
    type: Boolean,
    default: true
  }
});
const emit = defineEmits(['update:modelValue']);

// Transform items to match USelectMenu format
const transformedItems = computed(() => {
  return props.items.map(item => ({
    label: `${item.first_name} ${item.last_name}`,
    value: item.id,
    avatar: item.avatar
  }));
});

// Add a computed property for selectedIds with getter/setter
const selectedIds = computed({
  get() {
    return props.modelValue.map(item => item.id);
  },
  set(newIds) {
    const selectedProfiles = props.items.filter(item => newIds.includes(item.id));
    emit('update:modelValue', selectedProfiles);
  }
});

// Configure content position based on side prop
const contentConfig = computed(() => {
  const positions = {
    'bottom-right': { side: 'bottom', align: 'start' },
    'bottom-left': { side: 'bottom', align: 'end' },
    'top-right': { side: 'top', align: 'start' },
    'top-left': { side: 'top', align: 'end' }
  };
  return {
    ...positions[props.side] || positions['bottom-right'],
    class: `${props.size == 'xl' ? 'min-w-72' : props.size == 'lg' ? 'min-w-64' : props.size == 'md' ? 'min-w-52' : 'min-w-40'}`
  };
});
</script>

<template>
  <USelectMenu v-model="selectedIds" :items="transformedItems" :placeholder="placeholder" :size="size"
    :disabled="disabled" :content="contentConfig" :search-input="searchable ? { placeholder: 'Search...' } : false"
    multiple value-key="value">
    <!-- Button -->
    <template #default="{ modelValue }">
      <div class="flex items-center gap-1">
        <span v-if="showAssignedTo && modelValue?.length">Assigned To: </span>
        <div class="flex items-center -my-0.5 gap-1">
          <SvgAvatar v-for="item in modelValue" :key="item"
            :initials="transformedItems.find(i => i.value === item)?.avatar.initials"
            :color="transformedItems.find(i => i.value === item)?.avatar.color" :size="size === 'lg' ? 24 : 20" />
        </div>
        <span v-if="!modelValue?.length" class="text-gray-400">{{ placeholder }}</span>
      </div>
    </template>

    <!-- Dropdown Items -->
    <template #item="{ item }">
      <div class="flex justify-between items-center w-full grow">
        <div class="flex items-center gap-2">
          <SvgAvatar :initials="item.avatar.initials" :color="item.avatar.color" :size="size === 'lg' ? 28 : 24" />
          <span>{{ item.label }}</span>
        </div>

        <!-- Trailing Icon (checkmark) -->
        <UIcon v-if="selectedIds.includes(item.value)" name="i-lucide:check" class="w-5 h-5" />
      </div>
    </template>
  </USelectMenu>
</template>